import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type {
  Matter,
  MatterStatus,
  JurisdictionalThread,
  Filing,
  Artifact,
  Version,
  Deadline,
  AuditEvent,
  TimeEntry,
  RateCardEntry,
} from '@jerico/shared';

// --- Matters ---

export function useMatters(status?: MatterStatus) {
  const params = status ? `?status=${status}` : '';
  return useQuery({
    queryKey: ['matters', status],
    queryFn: () => api.get<{ matters: Matter[]; lastKey?: unknown }>(`/matters${params}`),
  });
}

export function useMatter(matterId: string) {
  return useQuery({
    queryKey: ['matter', matterId],
    queryFn: () => api.get<{ matter: Matter; counts: { threads: number; artifacts: number; deadlines: number } }>(`/matters/${matterId}`),
    enabled: !!matterId,
  });
}

export function useCreateMatter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      clientId: string;
      matterType: string;
      title: string;
      responsibleAttorneyId?: string;
      teamUserIds?: string[];
      organizationId?: string;
      contactId?: string;
      billingContactId?: string;
    }) =>
      api.post<{ matter: Matter }>('/matters', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['matters'] }),
  });
}

export function useUpdateMatter(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { title?: string; status?: MatterStatus; matterType?: string; responsibleAttorneyId?: string | null; teamUserIds?: string[] }) =>
      api.put<{ matter: Matter }>(`/matters/${matterId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['matters'] });
      qc.invalidateQueries({ queryKey: ['matter', matterId] });
    },
  });
}

export function useUpdateEthicsWall(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { action: 'add' | 'remove'; userId: string }) =>
      api.put<{ ethicsWallUserIds: string[] }>(`/matters/${matterId}/ethics-wall`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['matter', matterId] });
    },
  });
}

export function useDeleteMatter(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ deleted: boolean }>(`/matters/${matterId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['matters'] });
    },
  });
}

export function useMatterContents(matterId: string) {
  return useQuery({
    queryKey: ['matterContents', matterId],
    queryFn: () => api.get<{ threads: JurisdictionalThread[]; filings: Filing[]; artifacts: Artifact[]; deadlines: Deadline[]; auditEvents: AuditEvent[]; timeEntries: TimeEntry[] }>(`/matters/${matterId}/contents`),
    enabled: !!matterId,
  });
}

export function useExportMatter(matterId: string) {
  return useMutation({
    mutationFn: () => api.post<{ matter: Matter; threads: JurisdictionalThread[]; filings: Filing[]; artifacts: unknown[]; deadlines: Deadline[] }>(`/matters/${matterId}/export`),
  });
}

export function useAnalyzeMatter(matterId: string) {
  return useMutation({
    mutationFn: () =>
      api.get<{
        analysis: {
          risks: Array<{ title: string; detail: string; severity: 'high' | 'medium' | 'low' }>;
          insights: Array<{ title: string; detail: string }>;
          actions: Array<{ title: string; detail: string; priority: 'urgent' | 'soon' | 'later' }>;
        };
        error?: string;
      }>(`/ai/analyze-matter/${matterId}`),
  });
}

// --- Threads ---

export function useThreads(matterId: string) {
  return useQuery({
    queryKey: ['threads', matterId],
    queryFn: () => api.get<{ threads: JurisdictionalThread[] }>(`/matters/${matterId}/threads`),
    enabled: !!matterId,
  });
}

export function useCreateThread(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { jurisdictionCountry: string; authority: string; threadType: string; jurisdictionRegion?: string; legalRegime?: string; referenceNumbers?: string[]; localCounselContacts?: { name: string; email: string; phone?: string }[] }) =>
      api.post<{ thread: JurisdictionalThread }>(`/matters/${matterId}/threads`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['threads', matterId] });
      qc.invalidateQueries({ queryKey: ['matter', matterId] });
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
    },
  });
}

export function useUpdateThread(matterId: string, threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { threadStatus?: string; jurisdictionCountry?: string; authority?: string; referenceNumbers?: string[] }) =>
      api.put<{ thread: JurisdictionalThread }>(`/matters/${matterId}/threads/${threadId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['threads', matterId] });
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
    },
  });
}

export function useDeleteThread(matterId: string, threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ deleted: boolean }>(`/matters/${matterId}/threads/${threadId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['threads', matterId] });
      qc.invalidateQueries({ queryKey: ['matter', matterId] });
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
    },
  });
}

// --- Filings ---

export function useFilings(matterId: string, threadId?: string) {
  const params = threadId ? `?threadId=${threadId}` : '';
  return useQuery({
    queryKey: ['filings', matterId, threadId],
    queryFn: () => api.get<{ filings: Filing[] }>(`/matters/${matterId}/filings${params}`),
    enabled: !!matterId,
  });
}

export function useCreateFiling(matterId: string, threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { filingCode: string; filedDate?: string; filingMethod?: string; officialReceiptId?: string; packetArtifactIds?: string[] }) =>
      api.post<{ filing: Filing }>(`/matters/${matterId}/threads/${threadId}/filings`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['filings', matterId] });
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
    },
  });
}

export function useUpdateFiling(matterId: string, filingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { filingCode?: string; filedDate?: string; filingMethod?: string; threadId: string }) =>
      api.put<{ filing: Filing }>(`/matters/${matterId}/filings/${filingId}?threadId=${data.threadId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['filings', matterId] });
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
    },
  });
}

export function useDeleteFiling(matterId: string, filingId: string, threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.delete(`/matters/${matterId}/filings/${filingId}?threadId=${threadId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['filings', matterId] });
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
    },
  });
}

// --- Artifacts ---

export function useArtifacts(matterId: string, filters?: { threadId?: string; filingId?: string; artifactType?: string }) {
  const params = new URLSearchParams();
  if (filters?.threadId) params.set('threadId', filters.threadId);
  if (filters?.filingId) params.set('filingId', filters.filingId);
  if (filters?.artifactType) params.set('artifactType', filters.artifactType);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return useQuery({
    queryKey: ['artifacts', matterId, filters],
    queryFn: () => api.get<{ artifacts: Artifact[] }>(`/matters/${matterId}/artifacts${qs}`),
    enabled: !!matterId,
  });
}

export function useUploadArtifact(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { fileName: string; mimeType: string; documentType: string; direction?: string; language?: string; confidentialityLevel?: string; threadId?: string; filingId?: string }) =>
      api.post<{ artifact: Artifact; uploadUrl: string }>(`/matters/${matterId}/artifacts/upload`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['artifacts', matterId] });
      qc.invalidateQueries({ queryKey: ['matter', matterId] });
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
    },
  });
}

export function useGetArtifact(matterId: string, artifactId: string) {
  return useQuery({
    queryKey: ['artifact', matterId, artifactId],
    queryFn: () => api.get<{ artifact: Artifact; downloadUrl: string }>(`/matters/${matterId}/artifacts/${artifactId}`),
    enabled: !!matterId && !!artifactId,
  });
}

export function useUpdateArtifact(matterId: string, artifactId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.put<{ artifact: Artifact }>(`/matters/${matterId}/artifacts/${artifactId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['artifacts', matterId] });
      qc.invalidateQueries({ queryKey: ['artifact', matterId, artifactId] });
    },
  });
}

export function useDeleteArtifact(matterId: string, artifactId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ deleted: boolean }>(`/matters/${matterId}/artifacts/${artifactId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['artifacts', matterId] });
      qc.invalidateQueries({ queryKey: ['matter', matterId] });
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
    },
  });
}

// --- Versions ---

export function useVersions(matterId: string, artifactId: string) {
  return useQuery({
    queryKey: ['versions', matterId, artifactId],
    queryFn: () => api.get<{ versions: Version[] }>(`/matters/${matterId}/artifacts/${artifactId}/versions`),
    enabled: !!matterId && !!artifactId,
  });
}

export function useCreateVersion(matterId: string, artifactId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { fileName: string; mimeType: string; changeReason?: string; isOfficial?: boolean }) =>
      api.post<{ version: Version; uploadUrl: string }>(`/matters/${matterId}/artifacts/${artifactId}/versions`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['versions', matterId, artifactId] });
      qc.invalidateQueries({ queryKey: ['artifact', matterId, artifactId] });
    },
  });
}

// --- Deadlines ---

export function useAllDeadlines(status?: string) {
  const params = status ? `?status=${status}` : '';
  return useQuery({
    queryKey: ['deadlines', 'all', status],
    queryFn: () => api.get<{ deadlines: Deadline[]; lastKey?: unknown }>(`/deadlines${params}`),
  });
}

export function useCreateDeadline(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { title: string; dueAt: string; timezone?: string; threadId?: string; description?: string; responsibleUserId?: string }) =>
      api.post<{ deadline: Deadline }>(`/matters/${matterId}/deadlines`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deadlines'] });
      qc.invalidateQueries({ queryKey: ['matter', matterId] });
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
    },
  });
}

export function useUpdateDeadline(matterId: string, deadlineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { title?: string; dueAt?: string; status?: string; description?: string; responsibleUserId?: string }) =>
      api.put<{ deadline: Deadline }>(`/matters/${matterId}/deadlines/${deadlineId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deadlines'] });
      qc.invalidateQueries({ queryKey: ['matter', matterId] });
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
    },
  });
}

export function useDeleteDeadline(matterId: string, deadlineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ deleted: boolean }>(`/matters/${matterId}/deadlines/${deadlineId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deadlines'] });
      qc.invalidateQueries({ queryKey: ['matter', matterId] });
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
    },
  });
}

// --- Audit ---

export function useAuditLog(matterId: string, limit?: number) {
  const params = limit ? `?limit=${limit}` : '';
  return useQuery({
    queryKey: ['audit', matterId],
    queryFn: () => api.get<{ auditEvents: AuditEvent[]; lastKey?: unknown }>(`/matters/${matterId}/audit${params}`),
    enabled: !!matterId,
  });
}

// --- Jurisdictions ---

export function useJurisdictions() {
  return useQuery({
    queryKey: ['jurisdictions'],
    queryFn: () => api.get<{ jurisdictions: unknown[] }>('/jurisdictions'),
  });
}

export function useCreateJurisdiction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { country: string; code: string; name: string; practiceAreas: string[]; legalRegimes?: string[] }) =>
      api.post<{ jurisdiction: unknown }>('/jurisdictions', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jurisdictions'] }),
  });
}

// --- Time Entries ---

export function useTimeEntries(params?: { userId?: string; startDate?: string; endDate?: string }) {
  const qs = new URLSearchParams();
  if (params?.userId) qs.set('userId', params.userId);
  if (params?.startDate) qs.set('startDate', params.startDate);
  if (params?.endDate) qs.set('endDate', params.endDate);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery({
    queryKey: ['timeEntries', params],
    queryFn: () => api.get<{ timeEntries: TimeEntry[]; lastKey?: unknown }>(`/time-entries${query}`),
  });
}

export function useCreateTimeEntry(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      description: string;
      durationMinutes: number;
      hourlyRateCents: number;
      date: string;
      billable?: boolean;
      clientId: string;
      referenceId?: string;
      referenceType?: string;
      referenceLabel?: string;
    }) => api.post<{ timeEntry: TimeEntry }>(`/matters/${matterId}/time-entries`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
      qc.invalidateQueries({ queryKey: ['timeEntries'] });
    },
  });
}

export function useUpdateTimeEntry(matterId: string, timeEntryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      description?: string;
      durationMinutes?: number;
      hourlyRateCents?: number;
      date?: string;
      billable?: boolean;
      status?: string;
    }) => api.put<{ timeEntry: TimeEntry }>(`/matters/${matterId}/time-entries/${timeEntryId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
      qc.invalidateQueries({ queryKey: ['timeEntries'] });
    },
  });
}

export function useDeleteTimeEntry(matterId: string, timeEntryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ deleted: boolean }>(`/matters/${matterId}/time-entries/${timeEntryId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['matterContents', matterId] });
      qc.invalidateQueries({ queryKey: ['timeEntries'] });
    },
  });
}

export function useBulkMarkBilled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { timeEntryIds: string[]; invoiceId: string; matterId: string }) =>
      api.post<{ updated: number }>('/time-entries/mark-billed', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timeEntries'] });
      qc.invalidateQueries({ queryKey: ['unbilledTime'] });
      qc.invalidateQueries({ queryKey: ['matterContents'] });
    },
  });
}

export function useBatchCreateTimeEntries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { entries: Array<Record<string, unknown>> }) =>
      api.post<{ created: number; failed: number; results: Array<Record<string, unknown>> }>('/time-entries/batch', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['timeEntries'] }),
  });
}

// --- Rate Cards (Firm-level) ---

export function useRateCards() {
  return useQuery({
    queryKey: ['rateCards'],
    queryFn: () => api.get<{ rates: RateCardEntry[] }>('/rate-cards'),
  });
}

export function useUpsertFirmRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      targetType: 'user' | 'role';
      targetUserId?: string;
      targetRole?: string;
      rateCents: number;
      currency?: string;
    }) => api.put<{ rate: RateCardEntry }>('/rate-cards', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rateCards'] }),
  });
}

export function useDeleteFirmRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { targetType: string; targetId: string }) =>
      api.delete<{ deleted: boolean }>(`/rate-cards/${data.targetType}/${data.targetId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rateCards'] }),
  });
}

export function useMatterRates(matterId: string) {
  return useQuery({
    queryKey: ['matterRates', matterId],
    queryFn: () => api.get<{ rates: RateCardEntry[] }>(`/matters/${matterId}/rate-cards`),
    enabled: !!matterId,
  });
}

export function useUpsertMatterRate(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { targetType: 'user' | 'role'; targetUserId?: string; targetRole?: string; rateCents: number }) =>
      api.put<{ rate: RateCardEntry }>(`/matters/${matterId}/rate-cards`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['matterRates', matterId] }),
  });
}

export function useDeleteMatterRate(matterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { targetType: string; targetId: string }) =>
      api.delete<{ deleted: boolean }>(`/matters/${matterId}/rate-cards/${params.targetType}/${params.targetId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['matterRates', matterId] }),
  });
}

export function useResolveRate(matterId: string) {
  return useQuery({
    queryKey: ['resolveRate', matterId],
    queryFn: () => api.get<{ rateCents: number | null; source: string | null }>(`/matters/${matterId}/resolve-rate`),
    enabled: !!matterId,
  });
}
